import { useCallback, useState } from 'react';
import { useMedplum } from '@medplum/react';
import { Appointment } from '@medplum/fhirtypes';
import { RESCHEDULED_CODING } from '@bariendo/lib/codings';
import { APPOINTMENT_STATUS_RESCHEDULED } from '@bariendo/lib/constants';
import { getOperationOutcomeMessage, isVersionConflictOutcome } from '@bariendo/lib/error';
import { isAppointmentRescheduled } from '@/pages/SchedulingPage/utils/appointments';
import { alert } from '@/services/alert';

/** SNOMED cancelationReason for rescheduled appointments */
const RESCHEDULED_CANCELATION_REASON = {
  coding: [RESCHEDULED_CODING],
};

/**
 * Hook to update an appointment's status.
 * Returns the handler and loading state for use in status dropdowns.
 */
export function useAppointment(
  appointment: Appointment | undefined,
  onStatusChange?: (updatedAppointment: Appointment) => void
) {
  const medplum = useMedplum();
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const updateStatus = useCallback(
    async (newStatus: string) => {
      if (!appointment?.id) {
        alert.error('Cannot update status: missing appointment');
        return;
      }

      try {
        setIsUpdatingStatus(true);

        const isRescheduled = newStatus === APPOINTMENT_STATUS_RESCHEDULED;

        let patch: Array<{ op: 'test' | 'replace' | 'add' | 'remove'; path: string; value?: unknown }>;

        if (isRescheduled) {
          const cancelationReasonOp =
            appointment.cancelationReason !== undefined
              ? { op: 'replace' as const, path: '/cancelationReason', value: RESCHEDULED_CANCELATION_REASON }
              : { op: 'add' as const, path: '/cancelationReason', value: RESCHEDULED_CANCELATION_REASON };
          patch = [
            { op: 'test', path: '/meta/versionId', value: appointment.meta?.versionId },
            cancelationReasonOp,
            { op: 'replace', path: '/status', value: 'cancelled' as Appointment['status'] },
          ];
        } else {
          patch = [
            { op: 'test', path: '/meta/versionId', value: appointment.meta?.versionId },
            ...(isAppointmentRescheduled(appointment) ? [{ op: 'remove' as const, path: '/cancelationReason' }] : []),
            { op: 'replace', path: '/status', value: newStatus as Appointment['status'] },
          ];
        }

        const updatedResource = await medplum.patchResource('Appointment', appointment.id, patch);
        alert.success('Status updated');
        onStatusChange?.(updatedResource);
      } catch (error) {
        if (isVersionConflictOutcome(error)) {
          alert.error(
            'Failed to update status: the appointment was updated in the meantime. Please close and reopen the popover, then try again.'
          );
          return;
        }

        alert.error('Failed to update status: ' + getOperationOutcomeMessage(error));
      } finally {
        setIsUpdatingStatus(false);
      }
    },
    [medplum, appointment, onStatusChange]
  );

  return { updateStatus, isUpdatingStatus };
}
